import { dollars, dollarsToNum } from "@oneethos/shared"

export const validateSolarCost = (value: string, minimumAmount: number): string => {
  return dollarsToNum(value) >= minimumAmount
    ? ''
    : `The minimum solar price is ${dollars(minimumAmount)}`
}

export const validateRoofCost = (
  value: string,
  systemCost: number,
  roofCostCap: number | undefined,
  hasRoofInput: boolean,
): string => {
  if (!hasRoofInput) return ''
  if (roofCostCap === undefined) return ''

  const roofCost = dollarsToNum(value)
  if (!roofCost || !systemCost) return ''
  
  const roofPct = (roofCost / systemCost) * 100
  const normalizedPct = Math.round(roofPct * 100) / 100

  return normalizedPct > roofCostCap
    ? `Roof price should not be greater than ${roofCostCap}% of total system cost`
    : ''
}

export const batteryPricePerKwhValidationMessage = (
  storageAmount: number,
  capacityKwh: number,
  maxPerKwh: number | undefined,
  isTeslaEquipment: boolean
): string => {
  if (maxPerKwh == null || maxPerKwh <= 0 || storageAmount <= 0 || capacityKwh <= 0) {
    return ''
  }
  return (storageAmount / capacityKwh) > maxPerKwh
    ? `${isTeslaEquipment ? 'Powerwall' : 'Storage'} price should not be greater than ${dollars(maxPerKwh)} per kWh.`
    : ''
}

export const storageSolarPctCapMessage = (
  storageValue: string | undefined,
  solarCost: string | undefined,
  isStorageOnly: boolean,
  storageCostCap: number | undefined,
  isTeslaEquipment: boolean
): string => {

  if (storageCostCap === undefined) return ''

  const storageAmount = dollarsToNum(storageValue ?? '')
  const solarAmount = dollarsToNum(solarCost ?? '')
  if (!isStorageOnly && storageAmount > 0 && solarAmount > 0) {
    const storagePct = (storageAmount / solarAmount) * 100
    const normalizedPct = Math.round(storagePct * 100) / 100
    if (normalizedPct > storageCostCap)
      return `${isTeslaEquipment ? 'Powerwall' : 'Storage'} price should not be greater than ${storageCostCap}% of solar price.`
  }
  return ''
}

export const validateStoragePrice = (
  value: string,
  {
    storageQty, teslaPowerwallQty, solarCost,
    isStorageOnly, isTeslaEquipment, storageCostCap,
  }: {
    storageQty: string
    teslaPowerwallQty: string
    solarCost: string
    isStorageOnly: boolean
    isTeslaEquipment: boolean
    storageCostCap: number | undefined
  }
): string => {
  const storageAmount = dollarsToNum(value)
  const pctCapErr = storageSolarPctCapMessage(value, solarCost, isStorageOnly, storageCostCap, isTeslaEquipment)
  if (pctCapErr) return pctCapErr

  if (storageAmount > 0 && !isStorageOnly && !isTeslaEquipment) {
    const sq = parseInt(storageQty) || 0
    return sq > 0 ? '' : 'Storage Quantity is required'
  }

  if (isTeslaEquipment) {
    if (storageAmount === 0) return ''
    const pwQty = parseInt(teslaPowerwallQty) || 0
    return pwQty > 0 ? '' : 'Powerwall Quantity is required'
  }

  if (isStorageOnly) {
    if (storageAmount === 0) return ''
    if (storageAmount < 10000) return `Battery Total Price should be minimum ${dollars(10000)}`
    const sq = parseInt(storageQty) || 0
    return sq > 0 ? '' : 'Battery Quantity is required'
  }

  return ''
}

export const validatePowerwallQty = (value: string, storagePriceAmount: number): string => {
  const qty = parseInt(value) || 0
  if (qty === 0) return storagePriceAmount === 0 ? '' : 'Powerwall Quantity is required'
  return storagePriceAmount > 0 ? '' : 'Powerwall Total Price is required'
}

export const validateStorageQty = (value: string, storageAmount: number, isStorageOnly: boolean): string => {
  const qty = parseInt(value) || 0
  if (qty === 0)
    return storageAmount === 0
      ? ''
      : isStorageOnly ? 'Battery Quantity is required' : 'Storage Quantity is required'
  return storageAmount > 0
    ? ''
    : isStorageOnly ? 'Battery Total Price is required' : 'Storage Price is required'
}

export const validateHvacCost = (
  value: string,
  systemCost: number,
  hvacCostCap: number | undefined,
  hasHvacInput: boolean,
): string => {
  if (!hasHvacInput) return ''
  if (hvacCostCap === undefined) return ''

  const hvacCost = dollarsToNum(value)
  if (!hvacCost || !systemCost) return ''

  const hvacPct = (hvacCost / systemCost) * 100
  const normalizedPct = Math.round(hvacPct * 100) / 100
  return normalizedPct > hvacCostCap
    ? `HVAC price should not be greater than ${hvacCostCap}% of total system cost.`
    : ''
}